import { Button, GlobalError, P } from '@dnb/eufemia';
import { Field, Form, Wizard } from '@dnb/eufemia/extensions/forms';
import { ApiDto } from '@portals/shared/portal/ApiDto';
import {
  AppApiEntitlementRequestSchemaV2Form,
  appApiEntitlementRequestSchemaV2Form,
  CreateAppApiEntitlementsResponseDto,
} from '@portals/shared/portal/AppApiEntitlementDto';
import { AccessTypes, AppWithEntitlementsDto } from '@portals/shared/portal/AppDto';
import Markdown from '@portals/shared-frontend/components/Markdown';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {} from 'react-router-dom';
import useSWR, { mutate } from 'swr';

import { updateEntitlement } from '@/api/app';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';
import useAuth from '@/hooks/useAuth';

import SelectIntegrationServices from '../components/SelectIntegrationServices';
import AdditionalInfo from './components/AdditionalInfo';
import EditScopes from './components/EditScopes';
import Success from './components/Success';
import Summary from './components/Summary';

const ENTITLEMENTS_FORM_ID = `EntitlementsForm`;
const WIZARD_CONTAINER_ID = `EntitlementsWizardContainer`;

export default function Entitlements() {
  const navigate = useNavigate();

  const { setActiveIndex } = Wizard.useStep(WIZARD_CONTAINER_ID);

  const { data: apis, isValidating: apisLoading } = useSWR<ApiDto[]>(`/api?attachable=true`);
  const { isInternal: isDnbUser, isLoading: userLoading } = useAuth();

  const { teamId, appId, apiId } = useParams();
  const requestType = apiId ? 'edit' : 'attach';
  const { data: app, isValidating: appLoading } = useSWR<AppWithEntitlementsDto>(`/apps/${appId}`);

  const [selectedApi, setSelectedApi] = useState<ApiDto | undefined>();
  const [success, setSuccess] = useState<CreateAppApiEntitlementsResponseDto | null>(null);

  const entitlement = useMemo(() => {
    return app?.currentApiAndScopeEntitlements.find((entitlement) => entitlement.api.id === selectedApi?.id);
  }, [selectedApi?.id, app?.currentApiAndScopeEntitlements]);
  // Set form data when entitlement changes

  const initialLiveScopes = useMemo(() => {
    const liveScopes =
      entitlement?.scopeEntitlements.filter((scope) => scope.liveMode).map(({ scopeName }) => scopeName) || [];
    return new Set(liveScopes);
  }, [entitlement]);

  const scopes = useMemo(() => {
    if (!selectedApi || !selectedApi.scopes) {
      return [];
    }
    return selectedApi.scopes.map((scope) => ({
      id: scope.id,
      name: scope.name,
      description: scope.description,
    }));
  }, [selectedApi]);
  useEffect(() => {
    const liveScopes =
      entitlement?.scopeEntitlements.filter((scope) => scope.liveMode).map(({ scopeName }) => scopeName) || [];
    const testScopes =
      entitlement?.scopeEntitlements.filter((scope) => !scope.liveMode).map(({ scopeName }) => scopeName) || [];

    Form.setData(ENTITLEMENTS_FORM_ID, {
      scopes: scopes.map((scope) => ({
        ...scope,
        test: testScopes.includes(scope.name),
        live: liveScopes.includes(scope.name),
      })),
      requestType,
      serviceNowMetaData: undefined,
      termsOfUseAccepted: false,
      useCase: '',
    });
  }, [entitlement, requestType, scopes]);

  //if apiId is provided, set selectedApi to the api with that id - edit scope flow
  useEffect(() => {
    if (apiId && apis) {
      const api = apis.find((api) => api.id === apiId);
      if (api) {
        setSelectedApi(api);
      }
    }
  }, [apiId, apis]);

  const apiToAttach = useMemo(() => {
    if (apis && app) {
      return apis
        .filter((api) => api.slug !== 'developer-portal-api')
        .filter((api) => (app.internal ? !api.isPublic : api.isPublic))
        .filter((api) => (app.accessType ? api.accessTypes?.includes(app.accessType) : true))
        .filter((api) => !app.currentApiAndScopeEntitlements.map((ent) => ent.api.id).includes(api.id));
    }
    return undefined;
  }, [apis, app]);

  const { data } = Form.useData<AppApiEntitlementRequestSchemaV2Form>(ENTITLEMENTS_FORM_ID);
  const needsAdditionalInfo = useMemo(() => {
    if (!selectedApi && !data?.scopes) {
      return false;
    }
    const entitlementScopes = entitlement?.scopeEntitlements ?? [];

    const liveScopesToKeep = data.scopes.filter((scope) => scope.live).map((scope) => scope.name);
    const testScopesToKeep = data.scopes.filter((scope) => scope.test).map((scope) => scope.name);

    const currentLiveScope =
      entitlementScopes.filter((scope) => scope.liveMode).map(({ scopeName }) => scopeName) || [];
    const currentTestScope =
      entitlementScopes.filter((scope) => !scope.liveMode).map(({ scopeName }) => scopeName) || [];

    //isRequestingNewLiveScopes is true only if liveScopesToKeep has newer scopes than currentLiveAppScopeEntitlements
    const isRequestingNewLiveScopes = liveScopesToKeep.some((s) => !currentLiveScope.includes(s));

    //isRequestingNewTestScopes is true only if testScopesToKeep has newer scopes than currentTestAppScopeEntitlements
    const isRequestingNewTestScopes = testScopesToKeep.some((s) => !currentTestScope.includes(s));

    const needsAdditionalInfo =
      isRequestingNewLiveScopes || (isRequestingNewTestScopes && selectedApi?.approvalStage === 'test');

    return needsAdditionalInfo;
  }, [data?.scopes, entitlement?.scopeEntitlements, selectedApi]);

  const isLoading = userLoading || appLoading || apisLoading;

  const onModalClose = () => {
    if (appId) {
      navigate(teamId ? `/team/${teamId}/application/${appId}` : `/application/${appId}`, {
        replace: true,
      });
    } else {
      navigate(teamId ? `/team/${teamId}` : '/profile/apps', {
        replace: true,
      });
    }
  };

  const onSubmit = async (data: AppApiEntitlementRequestSchemaV2Form) => {
    const scopes = data.scopes.filter((scope) => scope.live).map((scope) => scope.name);
    const testScopes = data.scopes.filter((scope) => scope.test).map((scope) => scope.name);

    if (!selectedApi) {
      throw new Error('api not found');
    }
    if (!app) {
      throw new Error('app not found');
    }
    const response = await updateEntitlement(app.id, selectedApi.id, {
      scopes,
      testScopes,
      useCase: data.useCase,
      serviceNowMetaData: needsAdditionalInfo ? data.serviceNowMetaData : undefined,
      termsOfUseAccepted: data.termsOfUseAccepted,
    });
    await mutate(`/apps/${app?.id}`);
    setSuccess(response);
  };

  if (appLoading || userLoading || apisLoading || !app) {
    return <LoadingModal />;
  }

  if (app && app.accessType === AccessTypes.O_AUTH_SYSTEM_TO_SYSTEM && app.internal && !app.serviceNowIdentifier) {
    return (
      <GlobalError
        code={null}
        text={
          <>
            <P>
              A ServiceNow identifier must be added to this app before access to an integration service can be
              requested.
            </P>{' '}
            <Button
              element={RouterLink}
              to={
                app.owner.type === 'team'
                  ? `/team/${app.owner.id}/application/${app.id}/edit`
                  : `/application/${app.id}/edit`
              }
              variant="tertiary"
            >
              Go to edit application
            </Button>{' '}
            to add ServiceNow identifier
          </>
        }
        title="ServiceNow identifier required"
      />
    );
  }

  if (success) {
    return (
      <ModalPage onClose={() => onModalClose()} skeleton={isLoading}>
        <Success onModalClose={onModalClose} response={success} />
      </ModalPage>
    );
  }

  return (
    <ModalPage
      onClose={() => onModalClose()}
      skeleton={isLoading}
      title={
        requestType === 'edit'
          ? `Update integration service access for app ${app?.name}`
          : `Request integration service access to app ${app?.name}`
      }
    >
      <Form.Handler<AppApiEntitlementRequestSchemaV2Form>
        id={ENTITLEMENTS_FORM_ID}
        onSubmit={onSubmit}
        schema={appApiEntitlementRequestSchemaV2Form}
      >
        <Wizard.Container id={WIZARD_CONTAINER_ID} initialActiveIndex={0} mode="strict">
          {requestType === 'attach' && (
            <Wizard.Step title="Select integration services">
              {apiToAttach && (
                <SelectIntegrationServices
                  apis={apiToAttach}
                  isDnbUser={isDnbUser}
                  onSelect={(api) => {
                    setSelectedApi(api);
                    setActiveIndex && setActiveIndex(1);
                  }}
                />
              )}
            </Wizard.Step>
          )}

          {app.accessType === AccessTypes.O_AUTH_SYSTEM_TO_SYSTEM && (
            <Wizard.Step title="Select Scopes">
              {selectedApi && (
                <EditScopes api={selectedApi} formId={ENTITLEMENTS_FORM_ID} initialLiveScopes={initialLiveScopes} />
              )}
            </Wizard.Step>
          )}

          {app.accessType === AccessTypes.O_AUTH_SYSTEM_TO_SYSTEM && needsAdditionalInfo && (
            <Wizard.Step title="Additional Information">
              {selectedApi && <AdditionalInfo api={selectedApi} />}
            </Wizard.Step>
          )}
          {requestType === 'attach' && selectedApi?.termsOfUse && (
            <Wizard.Step title="Terms of Use">
              <Form.Card>
                <Markdown>{selectedApi.termsOfUse}</Markdown>
                <Field.Boolean
                  label="I accept the terms of use"
                  name="termsOfUseAccepted"
                  path="/termsOfUseAccepted"
                  required
                />
              </Form.Card>
              <Wizard.Buttons />
            </Wizard.Step>
          )}
          <Wizard.Step title="Summary">
            {selectedApi && (
              <Summary api={selectedApi} formId={ENTITLEMENTS_FORM_ID} showAdditionalInfo={needsAdditionalInfo} />
            )}
          </Wizard.Step>
        </Wizard.Container>
      </Form.Handler>
    </ModalPage>
  );
}
